import { useState, useEffect, useCallback } from 'react';
import { 
  Truck, Search, Eye, Package, Building2, Store, ArrowRight, 
  Clock, CheckCircle, AlertTriangle, RefreshCw, Filter, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { transfersAPI } from '@/services/api';
import { InTransitControl } from '@/components/hq/InTransitControl';

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  approved: 'bg-blue-100 text-blue-700',
  processing: 'bg-amber-100 text-amber-700',
  packed: 'bg-indigo-100 text-indigo-700',
  dispatched: 'bg-purple-100 text-purple-700',
  in_transit: 'bg-cyan-100 text-cyan-700',
  delivered: 'bg-teal-100 text-teal-700',
  completed: 'bg-emerald-100 text-emerald-700',
  discrepancy: 'bg-red-100 text-red-700',
  resolved: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500'
};

const statusIcons = {
  draft: Clock,
  approved: CheckCircle,
  processing: Package,
  packed: Package,
  dispatched: Truck,
  in_transit: Truck,
  delivered: MapPin,
  completed: CheckCircle,
  discrepancy: AlertTriangle,
  resolved: CheckCircle,
  cancelled: AlertTriangle
};

const TransferOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [timeline, setTimeline] = useState([]);
  
  const { toast } = useToast();

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter && typeFilter !== 'all') params.transfer_type = typeFilter;
      
      const response = await transfersAPI.listOrders(params);
      setOrders(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch transfer orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, toast]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleOpenDetail = async (order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
    
    try {
      const response = await transfersAPI.getOrderTimeline(order.id);
      setTimeline(response.data.timeline || []);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  };

  const filteredOrders = orders.filter(order => 
    !search || 
    order.transfer_number?.toLowerCase().includes(search.toLowerCase()) ||
    order.source_name?.toLowerCase().includes(search.toLowerCase()) ||
    order.destination_name?.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = orders.filter(o => !['completed', 'cancelled', 'resolved'].includes(o.status)).length;
  const discrepancyCount = orders.filter(o => o.status === 'discrepancy').length;

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck className="w-6 h-6 text-purple-600" />
            Stock Transfers
          </h1>
          <p className="text-gray-500 mt-1">Monitor all stock transfer orders across locations</p>
        </div>
        <div className="flex items-center gap-3">
          {discrepancyCount > 0 && (
            <Badge className="bg-red-100 text-red-700 px-3 py-1">
              <AlertTriangle className="w-4 h-4 mr-1" />
              {discrepancyCount} Discrepancies
            </Badge>
          )}
          <Button variant="outline" onClick={fetchOrders} className="border-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <InTransitControl toast={toast} onChanged={fetchOrders} />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-800">{orders.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-blue-600">{activeCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">In Transit</p>
            <p className="text-2xl font-bold text-purple-600">
              {orders.filter(o => ['dispatched', 'in_transit'].includes(o.status)).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-emerald-600">
              {orders.filter(o => o.status === 'completed').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Discrepancy</p>
            <p className="text-2xl font-bold text-red-600">{discrepancyCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Search by transfer number or location..." 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-300"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] bg-gray-50 border-gray-300">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
                <SelectItem value="in_transit">In Transit</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="discrepancy">Discrepancy</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px] bg-gray-50 border-gray-300">
                <SelectValue placeholder="Transfer Type" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="warehouse_to_store">Warehouse → Store</SelectItem>
                <SelectItem value="store_to_store">Store → Store</SelectItem>
                <SelectItem value="warehouse_to_warehouse">WH → WH</SelectItem>
                <SelectItem value="hq_direct">HQ Direct</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading transfers...</div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No transfer orders found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-700">Transfer #</TableHead>
                  <TableHead className="font-semibold text-gray-700">Route</TableHead>
                  <TableHead className="font-semibold text-gray-700">Items</TableHead>
                  <TableHead className="font-semibold text-gray-700">Value</TableHead>
                  <TableHead className="font-semibold text-gray-700">Status</TableHead>
                  <TableHead className="font-semibold text-gray-700">Compliance</TableHead>
                  <TableHead className="font-semibold text-gray-700">Date</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map(order => {
                  const StatusIcon = statusIcons[order.status] || Clock;
                  return (
                    <TableRow key={order.id} className="hover:bg-gray-50">
                      <TableCell>
                        <span className="font-mono text-sm text-purple-600">{order.transfer_number}</span>
                        {order.stock_request_number && (
                          <p className="text-xs text-gray-400">Req: {order.stock_request_number}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <div className="flex items-center">
                            {order.source_type === 'warehouse' ? (
                              <Building2 className="w-4 h-4 text-purple-500 mr-1" />
                            ) : (
                              <Store className="w-4 h-4 text-teal-500 mr-1" />
                            )}
                            <span className="text-gray-700">{order.source_name}</span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                          <div className="flex items-center">
                            {order.destination_type === 'warehouse' ? (
                              <Building2 className="w-4 h-4 text-purple-500 mr-1" />
                            ) : (
                              <Store className="w-4 h-4 text-teal-500 mr-1" />
                            )}
                            <span className="text-gray-700">{order.destination_name}</span>
                          </div>
                        </div>
                        {order.destination_store_type === 'franchise' && (
                          <Badge className="mt-1 bg-orange-100 text-orange-700 text-xs">Franchise</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-800">{order.items?.length || 0} items</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-gray-800">{formatCurrency(order.grand_total)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[order.status]} flex items-center gap-1 w-fit`}>
                          <StatusIcon className="w-3 h-3" />
                          {order.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {order.requires_einvoice && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">E-Inv</Badge>
                          )}
                          {order.requires_eway_bill && (
                            <Badge className="bg-indigo-100 text-indigo-700 text-xs">E-Way</Badge>
                          )}
                          {!order.requires_einvoice && !order.requires_eway_bill && (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-500">
                          {order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDetail(order)}
                          className="text-gray-600 hover:text-purple-600"
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            Previous
          </Button>
          <span className="px-4 py-2 text-gray-600">Page {page} of {totalPages}</span>
          <Button variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            Next
          </Button>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Truck className="w-5 h-5 text-purple-600" />
              Transfer Order - {selectedOrder?.transfer_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="bg-gray-100 mb-4">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="items">Items ({selectedOrder.items?.length || 0})</TabsTrigger>
                <TabsTrigger value="dispatch">Dispatch Info</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <Label className="text-xs text-blue-600">Source</Label>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedOrder.source_type === 'warehouse' ? (
                        <Building2 className="w-5 h-5 text-purple-600" />
                      ) : (
                        <Store className="w-5 h-5 text-teal-600" />
                      )}
                      <div>
                        <p className="font-medium text-gray-800">{selectedOrder.source_name}</p>
                        <p className="text-sm text-gray-500">{selectedOrder.source_code} • {selectedOrder.source_gstin}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <Label className="text-xs text-emerald-600">Destination</Label>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedOrder.destination_type === 'warehouse' ? (
                        <Building2 className="w-5 h-5 text-purple-600" />
                      ) : (
                        <Store className="w-5 h-5 text-teal-600" />
                      )}
                      <div>
                        <p className="font-medium text-gray-800">{selectedOrder.destination_name}</p>
                        <p className="text-sm text-gray-500">{selectedOrder.destination_code} • {selectedOrder.destination_gstin}</p>
                        {selectedOrder.destination_store_type === 'franchise' && (
                          <Badge className="mt-1 bg-orange-100 text-orange-700 text-xs">Franchise</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs text-gray-500">Status</Label>
                    <Badge className={`mt-1 ${statusColors[selectedOrder.status]}`}>
                      {selectedOrder.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs text-gray-500">Transfer Type</Label>
                    <p className="font-medium text-gray-800 capitalize text-sm mt-1">
                      {selectedOrder.transfer_type?.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs text-gray-500">Grand Total</Label>
                    <p className="font-bold text-gray-800 mt-1">{formatCurrency(selectedOrder.grand_total)}</p>
                  </div>
                </div>

                {(selectedOrder.requires_einvoice || selectedOrder.requires_eway_bill) && (
                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <Label className="text-xs text-indigo-600">Compliance Requirements</Label>
                    <div className="flex gap-2 mt-2">
                      {selectedOrder.requires_einvoice && (
                        <Badge className="bg-indigo-100 text-indigo-700">E-Invoice Required</Badge>
                      )}
                      {selectedOrder.requires_eway_bill && (
                        <Badge className="bg-indigo-100 text-indigo-700">E-Way Bill Required</Badge>
                      )}
                    </div>
                  </div>
                )}

                {selectedOrder.receipt_info?.has_discrepancy && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <Label className="text-xs text-red-600">Discrepancy Reported</Label>
                    <p className="text-red-700 mt-1">{selectedOrder.receipt_info.discrepancy_notes || 'Discrepancy in received items'}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="items">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Approved</TableHead>
                      <TableHead className="text-right">Dispatched</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedOrder.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <p className="font-medium text-gray-800">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.sku} • HSN: {item.hsn_code}</p>
                        </TableCell>
                        <TableCell className="text-right">{item.approved_qty}</TableCell>
                        <TableCell className="text-right">{item.dispatched_qty || '-'}</TableCell>
                        <TableCell className="text-right">
                          {item.received_qty !== undefined && item.received_qty !== null ? (
                            <span className={item.received_qty < item.dispatched_qty ? 'text-red-600' : 'text-emerald-600'}>
                              {item.received_qty}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(item.transfer_price)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.line_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex justify-end p-4 border-t bg-gray-50">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Subtotal: {formatCurrency(selectedOrder.subtotal)}</p>
                    <p className="text-sm text-gray-500">Tax: {formatCurrency(selectedOrder.total_tax)}</p>
                    <p className="text-lg font-bold text-gray-800">Total: {formatCurrency(selectedOrder.grand_total)}</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="dispatch" className="space-y-4">
                {selectedOrder.dispatch_info?.dispatched_at ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <Label className="text-xs text-gray-500">Dispatched By</Label>
                      <p className="font-medium text-gray-800">{selectedOrder.dispatch_info.dispatched_by_name || '-'}</p>
                      <p className="text-sm text-gray-500">
                        {selectedOrder.dispatch_info.dispatched_at ? new Date(selectedOrder.dispatch_info.dispatched_at).toLocaleString('en-IN') : '-'}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <Label className="text-xs text-gray-500">Vehicle</Label>
                      <p className="font-medium text-gray-800">{selectedOrder.dispatch_info.vehicle_number || '-'}</p>
                      <p className="text-sm text-gray-500">{selectedOrder.dispatch_info.vehicle_type || '-'}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <Label className="text-xs text-gray-500">Driver</Label>
                      <p className="font-medium text-gray-800">{selectedOrder.dispatch_info.driver_name || '-'}</p>
                      <p className="text-sm text-gray-500">{selectedOrder.dispatch_info.driver_mobile || '-'}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <Label className="text-xs text-gray-500">Transporter</Label>
                      <p className="font-medium text-gray-800">{selectedOrder.dispatch_info.transporter_name || '-'}</p>
                    </div>
                    {selectedOrder.dispatch_info.dispatch_notes && (
                      <div className="col-span-2 p-3 bg-amber-50 rounded-lg">
                        <Label className="text-xs text-amber-600">Dispatch Notes</Label>
                        <p className="text-gray-700 mt-1">{selectedOrder.dispatch_info.dispatch_notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Not yet dispatched</p>
                  </div>
                )}

                {selectedOrder.receipt_info?.received_at && (
                  <div className="mt-6 pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-gray-700 font-medium block">Receipt Information</Label>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reconciling}
                        onClick={async () => {
                          setReconciling(true);
                          try {
                            const r = await transfersAPI.reconcileStock(selectedOrder.id);
                            const d = r.data;
                            toast({
                              title: d.fixed.length ? `Re-booked ${d.fixed.length} product(s) into stock` : 'Stock already matches this receipt',
                              description: d.fixed.length
                                ? d.fixed.map(f => `${f.product_name || f.product_id}: +${f.added_qty}`).join(', ')
                                : `${d.checked} lines checked${d.errors.length ? `, ${d.errors.length} errors` : ''}`,
                              variant: d.errors.length ? 'destructive' : undefined,
                            });
                            fetchOrders();
                          } catch (e) {
                            toast({ title: 'Reconcile failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
                          } finally { setReconciling(false); }
                        }}
                        data-testid="reconcile-stock-btn"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${reconciling ? 'animate-spin' : ''}`} /> Re-check store stock
                      </Button>
                    </div>
                    {selectedOrder.receipt_info?.stock_errors?.length > 0 && (
                      <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="receipt-stock-errors">
                        {selectedOrder.receipt_info.stock_errors.length} product(s) failed to enter stock during receipt — use “Re-check store stock”.
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <Label className="text-xs text-emerald-600">Received By</Label>
                        <p className="font-medium text-gray-800">{selectedOrder.receipt_info.received_by_name || '-'}</p>
                        <p className="text-sm text-gray-500">
                          {selectedOrder.receipt_info.received_at ? new Date(selectedOrder.receipt_info.received_at).toLocaleString('en-IN') : '-'}
                        </p>
                      </div>
                      {selectedOrder.receipt_info.receipt_notes && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <Label className="text-xs text-gray-500">Receipt Notes</Label>
                          <p className="text-gray-700 mt-1">{selectedOrder.receipt_info.receipt_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="timeline">
                <div className="space-y-4">
                  {timeline.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No timeline events</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                      {timeline.map((event, idx) => (
                        <div key={idx} className="relative pl-10 pb-6">
                          <div className="absolute left-2 w-4 h-4 rounded-full bg-purple-500 border-2 border-white" />
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-800 capitalize">{event.action?.replace('_', ' ')}</span>
                              <span className="text-xs text-gray-500">
                                {event.at ? new Date(event.at).toLocaleString('en-IN') : '-'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">By: {event.by_name || event.by}</p>
                            {event.notes && (
                              <p className="text-sm text-gray-500 mt-1">{event.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransferOrdersPage;
